import { defineStore } from "pinia";
import { ref, computed } from "vue";
import AuthService from "@/services/auth.service";
import type { AuthUser, LoginPayload, UserRoleCode, PermissionCode } from "@/models/auth.model";
import { ROLE_REDIRECTS } from "@/models/auth.model";

export const useAuthStore = defineStore("auth", () => {
  const user = ref<AuthUser | null>(AuthService.getUserFromStorage());
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isLoggedIn = computed(() => !!user.value);

  const roleCodes = computed<UserRoleCode[]>(
    () => user.value?.roles?.map((r) => r.code) ?? []
  );

  // Toutes les permissions de tous les rôles (+ délégations à intégrer plus tard)
  const permissionCodes = computed<PermissionCode[]>(() => {
    const perms = new Set<PermissionCode>();
    user.value?.roles?.forEach(role => {
      role.permissions?.forEach(p => perms.add(p.code as PermissionCode));
    });
    return Array.from(perms);
  });

  // Rôle de plus haut niveau (niveau le plus bas = plus de droits)
  const primaryRole = computed<UserRoleCode | null>(() => {
    if (!user.value?.roles?.length) return null;
    return [...user.value.roles]
      .sort((a, b) => a.niveau - b.niveau)[0].code;
  });

  // Route de redirection après login
  const redirectRoute = computed<string>(() => {
    if (!primaryRole.value) return "/malaw/en-attente";
    return ROLE_REDIRECTS[primaryRole.value] ?? "/malaw/dashboard";
  });

  function hasRole(role: UserRoleCode): boolean {
    return roleCodes.value.includes(role);
  }

  function hasAnyRole(roles: UserRoleCode[]): boolean {
    return roles.some(r => roleCodes.value.includes(r));
  }

  function hasPermission(code: PermissionCode): boolean {
    return permissionCodes.value.includes(code);
  }

  function hasAnyPermission(codes: PermissionCode[]): boolean {
    return codes.some(c => permissionCodes.value.includes(c));
  }

  // canDo = hasPermission (les délégations arriveront dans les permissions via /auth/me)
  function canDo(code: PermissionCode): boolean {
    return hasPermission(code);
  }

  async function login(payload: LoginPayload) {
    loading.value = true;
    error.value = null;
    try {
      const res = await AuthService.login(payload);
      user.value = res.user;
    } catch (e: any) {
      error.value = e?.response?.data?.message ?? "Erreur de connexion";
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    await AuthService.logout();
    user.value = null;
  }

  async function fetchMe() {
    try {
      user.value = await AuthService.me();
      // Mettre à jour le localStorage avec les nouvelles permissions/délégations
      localStorage.setItem("user", JSON.stringify(user.value));
    } catch {
      user.value = null;
    }
  }

  return {
    user, loading, error, isLoggedIn,
    roleCodes, permissionCodes, primaryRole, redirectRoute,
    hasRole, hasAnyRole, hasPermission, hasAnyPermission, canDo,
    login, logout, fetchMe,
  };
});
